import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Search, 
  FileText, 
  CreditCard, 
  Ship, 
  PackageCheck,
  MapPin,
  Clock,
  CheckCircle2
} from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';

const ShippingPage = () => {
  const [currency, setCurrency] = useState('USD');

  const steps = [
    {
      icon: Search,
      title: '1. Select Your Vehicle',
      description: 'Browse our inventory and choose the perfect vehicle. Use filters to narrow down by brand, model, price, and specifications.',
      details: [
        'View detailed vehicle information and photos',
        'Check vehicle condition reports',
        'Compare different options',
        'Save favorites for later'
      ]
    },
    {
      icon: FileText,
      title: '2. Submit Inquiry',
      description: 'Contact us with your vehicle selection. Our team will provide a detailed quotation including all costs.',
      details: [
        'Fill out the inquiry form',
        'Receive FOB and shipping quote',
        'Get documentation checklist',
        'Ask any questions'
      ]
    },
    {
      icon: CreditCard,
      title: '3. Make Payment',
      description: 'Once you confirm, make the payment via bank transfer. We accept international wire transfers and other secure methods.',
      details: [
        'Review and sign proforma invoice',
        'Make payment via TT/Bank transfer',
        'Payment confirmation within 24 hours',
        'Receive payment receipt'
      ]
    },
    {
      icon: PackageCheck,
      title: '4. Vehicle Preparation',
      description: 'After payment confirmation, we prepare your vehicle for export with all necessary documentation.',
      details: [
        'Vehicle inspection and preparation',
        'Export documentation processing',
        'Customs clearance preparation',
        'Loading arrangement'
      ]
    },
    {
      icon: Ship,
      title: '5. Shipping',
      description: 'Your vehicle is shipped to your destination port. We provide all shipping documents and tracking information.',
      details: [
        'Vehicle loaded onto vessel',
        'Bill of Lading issued',
        'Shipping schedule provided',
        'Regular status updates'
      ]
    },
    {
      icon: CheckCircle2,
      title: '6. Receive Your Vehicle',
      description: 'Clear customs at your destination port using the documents we provide, and receive your vehicle.',
      details: [
        'Receive all original documents',
        'Clear customs in your country',
        'Arrange port pickup',
        'Complete registration'
      ]
    }
  ];

  const shippingTimes = [
    { region: 'Asia', time: '2-4 weeks', ports: 'Singapore, Dubai, Thailand, Philippines' },
    { region: 'Africa', time: '4-6 weeks', ports: 'Tanzania, Kenya, South Africa, Nigeria' },
    { region: 'Middle East', time: '4-5 weeks', ports: 'UAE, Saudi Arabia, Qatar, Oman' },
    { region: 'Europe', time: '5-7 weeks', ports: 'UK, Germany, Belgium, Netherlands' },
    { region: 'Oceania', time: '5-6 weeks', ports: 'Australia, New Zealand, Fiji' },
    { region: 'South America', time: '6-8 weeks', ports: 'Chile, Peru, Colombia, Panama' }
  ];

  const documents = [
    'Bill of Lading (Original)',
    'Commercial Invoice',
    'Export Certificate',
    'Vehicle Registration',
    'Inspection Certificate',
    'Packing List',
    'Insurance Certificate (if applicable)',
    'Manufacturer\'s Certificate of Origin'
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Shipping & Export Process - Global Trade Car"
        description="Learn about our vehicle export process. Simple 6-step process from selection to delivery. Worldwide shipping to 50+ countries."
      />
      <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Shipping & Export Process
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We make international vehicle shipping simple and transparent. Here's how it works from start to finish.
          </p>
        </div>

        {/* Process Steps */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            6 Simple Steps to Your New Vehicle
          </h2>
          <div className="space-y-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card key={index} className="hover:shadow-xl transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                          <Icon className="h-8 w-8 text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                        <p className="text-gray-600 mb-4">{step.description}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {step.details.map((detail, idx) => (
                            <div key={`detail-${idx}`} className="flex items-start space-x-2">
                              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-600">{detail}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Shipping Times */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Estimated Shipping Times
          </h2>
          <Card>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {shippingTimes.map((item, index) => (
                  <div key={index} className="border-l-4 border-red-600 pl-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <MapPin className="h-5 w-5 text-red-600" />
                      <h3 className="text-lg font-semibold text-gray-900">{item.region}</h3>
                    </div>
                    <div className="flex items-center space-x-2 mb-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="text-red-600 font-semibold">{item.time}</span>
                    </div>
                    <p className="text-sm text-gray-600">Major ports: {item.ports}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> Shipping times are estimates and may vary based on vessel schedules, 
                  weather conditions, and port congestion. We'll provide specific timelines for your destination.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Documents Required */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Export Documents Provided
          </h2>
          <Card>
            <CardContent className="p-8">
              <p className="text-gray-600 mb-6">
                We provide all necessary export documents for smooth customs clearance at your destination. 
                Original documents are couriered to you or your clearing agent.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documents.map((doc, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="h-5 w-5 text-red-600" />
                    <span className="text-gray-900">{doc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Common Shipping Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">What's included in the price?</h3>
                <p className="text-sm text-gray-600">
                  FOB price includes the vehicle and all charges up to loading at the Japanese port. 
                  Shipping costs, destination charges, and import duties are separate and depend on your location.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">How do I track my shipment?</h3>
                <p className="text-sm text-gray-600">
                  Once shipped, we provide the Bill of Lading number and vessel information. 
                  You can track your vehicle's voyage online through shipping line websites.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Do you arrange insurance?</h3>
                <p className="text-sm text-gray-600">
                  Marine insurance is optional but highly recommended. We can arrange comprehensive 
                  insurance coverage for your vehicle during transit at competitive rates.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">What about customs clearance?</h3>
                <p className="text-sm text-gray-600">
                  We provide all necessary export documents. You'll need to arrange customs clearance 
                  in your country. We can recommend reliable clearing agents if needed.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-red-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Browse our inventory or contact us for a detailed shipping quote to your destination.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/vehicles">
              <button className="px-8 py-4 bg-white text-red-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold">
                View Vehicles
              </button>
            </a>
            <a href="/contact">
              <button className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-red-700 transition-colors font-semibold">
                Get Shipping Quote
              </button>
            </a>
          </div>
        </div>
      </div>

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default ShippingPage;
